import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import classNames from 'classnames';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import {
  DomainAttributes,
  DomainSetupSteps,
  oauth2ProxyCallbackPath,
} from '@loyalty/shared-common/gamification-api-client';
import { Feature, getParentDomain } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  QlfConfirmationDialog,
  colorPalette,
  useConfirmationDialog,
  useQlfTranslation,
} from '@qualifio/ui-commons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons/faTimesCircle';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { makeStyles } from '@mui/styles';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDomains } from '../SubdomainsContext';
import { useFeatures } from '../../features/FeaturesContext';
import { usePrograms } from '../../loyalty/ProgramsContext';

const useStyles = makeStyles(() => ({
  iconButton: {
    fontSize: '16px',
    color: colorPalette.grey.main,
    backgroundColor: 'inherit',
    minWidth: 40,
    '&:hover': {
      backgroundColor: 'transparent !important',
      color: colorPalette.grey.darkHover,
    },
  },
  deleteButton: {
    '&:hover': {
      color: colorPalette.red.main,
      backgroundColor: 'transparent !important',
    },
  },
  domainInfo: {
    verticalAlign: 'top !important',
    fontSize: '14px !important',
  },
  statusInfo: {
    verticalAlign: 'top !important',
  },
  verificationInfo: {
    padding: '10px',
    textAlign: 'justify',
  },
  domainButton: {
    verticalAlign: 'top !important',
    padding: '20px !important',
  },
}));

export function DomainTableRow({ subdomain }: { subdomain: DomainAttributes }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { programs } = usePrograms();
  const { deleteSubdomain } = useDomains();
  const { features } = useFeatures();
  const { remove: subdomainRemove } = features[Feature.DOMAINS_MANAGEMENT];

  const program = programs.find(program => program.id === subdomain.programId);

  const { confirmationDialogState, setConfirmationDialogState } = useConfirmationDialog();
  const openDeleteDomainDialog = (subdomain: DomainAttributes) => {
    setConfirmationDialogState({
      title: t('loyalty.confirm.deletion.button'),
      content:
        subdomain.developmentMode || !subdomain.setup[DomainSetupSteps.SSL_CERTIFICATE]
          ? t('subdomain.edition.deletion.message', { name: subdomain.domain })
          : t('subdomain.edition.delete.setup', {
              domain: subdomain.domain,
              program: program?.name,
            }),
      confirmButtonText: t('generic.actions.hard-delete'),
      cancelButtonText: t('generic.actions.cancel'),
      open: true,
      onConfirm: () => deleteSubdomain(subdomain),
    });
  };

  return (
    <TableRow
      key={subdomain.domain}
      sx={{
        color: colorPalette.grey.light,
        '&:last-child td, &:last-child th': { border: 0 },
      }}
    >
      <TableCell align="left" className={classes.domainInfo}>
        {subdomain.domain}
        {subdomain.developmentMode ? (
          <Box component="span" sx={{ ml: 1, fontSize: '12px', color: colorPalette.orange.main }}>
            ({t('subdomain.edition.development.mode.badge')})
          </Box>
        ) : null}
      </TableCell>

      <TableCell align="left" className={classes.domainInfo}>
        {program?.name}
      </TableCell>

      <TableCell align="left" className={classes.statusInfo}>
        <InfoBoxBasedOnSetup subdomain={subdomain} />
      </TableCell>

      <TableCell align="left" className={classes.domainButton}>
        <ButtonBasedOnSetup subdomain={subdomain} />
      </TableCell>
      <TableCell align="left" className={classes.domainButton}>
        <QlfConfirmationDialog {...confirmationDialogState} />
        <Tooltip title={subdomainRemove ? '' : t('shared.action.permission.required')}>
          <div>
            <IconButton
              className={classNames(classes.iconButton, classes.deleteButton)}
              onClick={() => openDeleteDomainDialog(subdomain)}
            >
              <FontAwesomeIcon icon={faTrash} />
            </IconButton>
          </div>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

const useStyles2 = makeStyles(() => ({
  verificationInfo: {
    textAlign: 'justify',
  },
  textFieldBox: {
    border: '1px solid',
    borderColor: colorPalette.grey.main,
    borderRadius: '5px',
    paddingLeft: '5px',
    marginBottom: '10px',
    backgroundColor: colorPalette.grey.light,
    marginTop: '10px',
    paddingTop: '10px',
    paddingBottom: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  failed: {
    color: colorPalette.red.main,
  },
}));

function InfoBoxBasedOnSetup({ subdomain }: { subdomain: DomainAttributes }) {
  const classes = useStyles2();
  const { t } = useQlfTranslation('translation');
  const isNew = subdomain.setup[DomainSetupSteps.TEXTFIELD_VERIFICATION] === undefined;
  const txtVerified = subdomain.setup[DomainSetupSteps.TEXTFIELD_VERIFICATION];
  const partiallyDeleted = subdomain.setup[DomainSetupSteps.PARTIALLY_DELETED];
  const domain = getParentDomain(subdomain.domain, false);

  if (subdomain.developmentMode) {
    return (
      <div className={classes.verificationInfo}>
        <div>{t('subdomain.edition.development.mode.description')}</div>
      </div>
    );
  }

  if (partiallyDeleted) {
    return (
      <div className={classes.verificationInfo}>
        <div className={classes.failed}>{t('subdomain.edition.partially.deleted.description')}</div>
      </div>
    );
  }
  if (isNew || !txtVerified) {
    return (
      <div className={classes.verificationInfo}>
        <div>{t('subdomain.edition.textfield.description')}</div>
        <div className={classes.textFieldBox}>
          {t('subdomain.edition.textfield.record', {
            verificaitonDomain: `_${subdomain.programId}.${domain}`,
            textfield: subdomain.textField,
          })}
        </div>
        <div>{t('subdomain.edition.textfield.help')}</div>
        {!isNew && !txtVerified ? (
          <div className={classes.failed}>
            {t('subdomain.edition.textfield.failed.description')}
          </div>
        ) : (
          ''
        )}
      </div>
    );
  }

  return (
    <ul>
      {!subdomain.setup[DomainSetupSteps.SSL_CERTIFICATE] && (
        <li className={classNames(classes.failed, classes.verificationInfo)}>
          {t('subdomain.edition.ssl.error')}
        </li>
      )}
      {!subdomain.setup[DomainSetupSteps.UPDATE_ROUTING] && (
        <li className={classNames(classes.failed, classes.verificationInfo)}>
          {t('subdomain.edition.redirection.error')}
        </li>
      )}
      {subdomain.setup[DomainSetupSteps.UPDATE_ROUTING] &&
        subdomain.setup[DomainSetupSteps.SSL_CERTIFICATE] && (
          <li className={classNames(classes.verificationInfo)}>
            {t('access-rule.login.redirecturi.description')}
            <strong>{` ${subdomain.domain}${oauth2ProxyCallbackPath}`}</strong>
          </li>
        )}
    </ul>
  );
}

function ButtonBasedOnSetup({ subdomain }: { subdomain: DomainAttributes }) {
  const [isLoading, setLoading] = useState(false);
  const { setupSubdomain } = useDomains();
  const { t } = useTranslation();

  if (subdomain.developmentMode) {
    return null;
  }

  return (
    <div>
      <Checks domain={subdomain} />
      <Button
        startIcon={
          <FontAwesomeIcon
            fontSize="inherit"
            icon={isLoading ? faSpinner : faCheck}
            spin={isLoading}
          />
        }
        disabled={isLoading}
        onClick={() => {
          setLoading(true);
          setupSubdomain(subdomain).finally(() => setLoading(false));
        }}
      >
        {t('subdomain.edition.verify')}
      </Button>
    </div>
  );
}

function Checks({ domain }: { domain: DomainAttributes }) {
  const { t } = useTranslation();

  const checks = useMemo(
    () => [
      {
        name: DomainSetupSteps.CNAME_VERIFICATION,
        label: t('subdomain.checks.CNAME_VERIFICATION.label'),
      },
      {
        name: DomainSetupSteps.TEXTFIELD_VERIFICATION,
        label: t('subdomain.checks.TEXTFIELD_VERIFICATION.label'),
      },
    ],
    [t],
  );

  return (
    <div>
      {checks.map(step => (
        <Box
          key={step.name}
          sx={theme => ({
            display: 'flex',
            justifyContent: 'space-between',
          })}
        >
          <Box sx={theme => ({ marginRight: theme.spacing(1) })}>{step.label}</Box>
          <div>
            {domain.setup[step.name] ? (
              <FontAwesomeIcon icon={faCheckCircle} color={colorPalette.green.main} />
            ) : (
              <FontAwesomeIcon icon={faTimesCircle} color={colorPalette.red.main} />
            )}
          </div>
        </Box>
      ))}
    </div>
  );
}
